import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { FormMultiSelectField, FormReadonlyField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridFilterChangeEvent,
} from '@progress/kendo-react-grid'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { RoleDto } from 'api/Identity/Types/Role'
import { InputFilterCell } from 'components/Filter/FilterCell'
import MoreButton from 'components/MoreButton/MoreButton'
import { successToast, Toast } from 'components/Toast/Toast'
import stringComparator from 'utils/stringComparator'
import { uniqueArrayOfObjects } from 'utils/unique/unique'

export interface EditRoleFormValues {
  id: string
  name: string
  permissionIds: number[]
}

interface Props {
  role: RoleDto
  permissions: PermissionDto[]

  onSubmit(values: EditRoleFormValues): Promise<void>
  setToast(toast: Toast): void
}

export interface EditRoleGeneralFormHandle {
  submit(): Promise<void>
}

export const EditRoleGeneralForm = forwardRef<EditRoleGeneralFormHandle, Props>(
  ({ role, permissions, setToast, onSubmit }, ref) => {
    const [newRole, setNewRole] = useState<RoleDto>(role)
    const [filter, setFilter] = useState<CompositeFilterDescriptor>({ logic: 'and', filters: [] })
    const [selectedPermissions, setSelectedPermissions] = useState<number[]>([])

    useEffect(() => {
      setNewRole(role)
      setSelectedPermissions([])
      setFilter({ logic: 'and', filters: [] })
    }, [role])

    useImperativeHandle(
      ref,
      () => ({
        submit: async () => {
          await onSubmit({
            id: newRole.id,
            name: newRole.name,
            permissionIds: newRole.permissions.map((x) => x.id),
          })
        },
      }),
      [newRole, onSubmit]
    )

    const handleFilterChange = async (event: GridFilterChangeEvent) => {
      setFilter(event.filter)
    }

    const handleAddPermissions = () => {
      setNewRole({
        ...newRole,
        permissions: uniqueArrayOfObjects(
          newRole.permissions.concat(
            selectedPermissions
              .map((permissionId) => permissions.find((x) => x.id === permissionId))
              .filter((x): x is PermissionDto => !!x)
          )
        ),
      })
      setSelectedPermissions([])
      setToast(successToast(`Permissions added.`))
    }

    const gridData = filterBy(newRole.permissions || [], filter).sort((a, b) =>
      stringComparator(a.name, b.name)
    )

    const availablePermissions = permissions.filter(
      ({ id }) => !newRole.permissions.some((permission) => permission.id === id)
    )

    return (
      <div className='flex h-full min-h-0 flex-col gap-6'>
        <div className='shrink-0 space-y-4'>
          <FormReadonlyField name='name' label='Role' value={newRole.name} />
          <div className='space-y-3'>
            <FormMultiSelectField
              name='selectedPermissions'
              label='Add Permissions'
              options={availablePermissions.map((permission) => ({
                id: permission.id,
                name: permission.name,
              }))}
              value={selectedPermissions}
              placeholder='Select permissions'
              allowClear
              showSearch
              onChange={(value) => {
                const nextValues = Array.isArray(value) ? value : value == null ? [] : [value]

                setSelectedPermissions(nextValues.map((item) => Number(item)))
              }}
            />
            <div className='flex justify-end'>
              <Button
                disabled={!selectedPermissions.length}
                onClick={handleAddPermissions}
                type='button'
              >
                Add
              </Button>
            </div>
          </div>
        </div>

        <div className='flex min-h-0 flex-1 flex-col gap-3'>
          <div className='shrink-0'>
            <h3 className='font-medium'>Assigned Permissions</h3>
            <p className='text-sm text-muted-foreground'>
              Review the current permissions and remove any entries you no longer want on this role.
            </p>
          </div>

          <div className='min-h-0 flex-1 overflow-hidden'>
            <Grid
              filterable
              resizable
              data={gridData}
              filter={filter}
              total={newRole.permissions.length}
              onFilterChange={handleFilterChange}
              style={{ zIndex: 0, height: '100%' }}
            >
              <Column
                title='Name'
                field='name'
                filterCell={InputFilterCell}
                className='table-columns__content'
              />
              <Column
                width={50}
                title=''
                filterCell={() => null}
                cell={(props: GridCellProps) => (
                  <td>
                    <MoreButton
                      items={[
                        {
                          name: 'Remove',
                          'data-test': 'remove',
                          onClick: () =>
                            setNewRole({
                              ...newRole,
                              permissions: newRole.permissions.filter(
                                (x) => x.id !== (props.dataItem as PermissionDto).id
                              ),
                            }),
                        },
                      ]}
                      data-test='more-button'
                    />
                  </td>
                )}
              />
            </Grid>
          </div>
        </div>
      </div>
    )
  }
)

EditRoleGeneralForm.displayName = 'EditRoleGeneralForm'
